import { useState, useEffect, useMemo } from 'react';
import { TvaToggle, useTva, tvaPct, tvaAmount } from './TvaToggle';
import axios from 'axios';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Search, Building2, Truck, Plus, Trash2, FileText, Briefcase, ArrowLeft,
  Globe, Database, ShieldCheck, Landmark, FileSignature,
  BarChart3, ClipboardList, Scale, Receipt, Award, Rocket
} from 'lucide-react';
import { toast } from 'sonner';

const fmtXAF = (n) => new Intl.NumberFormat('fr-FR').format(Math.round(n || 0));

const OBJECTIFS = [
  'Immatriculation de la société',
  'Digitalisation (site + CRM)',
  'Étude & structuration',
  'Développement / lancement',
  'Régularisation administrative',
  'Autre',
];

const FORMES = [
  'SARL',
  'SARLU (unipersonnelle)',
  'SA',
  'SAS',
  'Entreprise individuelle',
  'GIE',
  'ONG / Association',
  'Autre',
];

const SECTEURS = [
  'Commerce & distribution',
  'Services',
  'Industrie / production',
  'Agriculture',
  'BTP / immobilier',
  'Tech & digital',
  'Santé',
  'Éducation & formation',
  'Autre',
];

const DELAIS = [
  '1 semaine (express)',
  '2 - 3 semaines',
  '1 mois',
  '2 mois',
  '> 2 mois',
];

const TRANSPORT_MODES = [
  'Livraison numérique (email/lien)',
  'Retrait sur place',
  'Livraison locale',
  'Transport routier',
];

// Catalogue par défaut des prestations création d'entreprise
const DEFAULT_PRESTATIONS = [
  { key: 'rccm',            designation: 'Immatriculation RCCM (registre du commerce)', icon: 'scale',        prix: 250000, qte: 1, checked: true  },
  { key: 'nif',             designation: 'Obtention du NIF (numéro d\'identification fiscale)', icon: 'receipt',      prix: 120000, qte: 1, checked: true  },
  { key: 'consulaire',      designation: 'Attestation consulaire',                       icon: 'award',        prix: 80000,  qte: 1, checked: false },
  { key: 'statuts',         designation: 'Rédaction des statuts & assemblées',           icon: 'file-signature', prix: 200000, qte: 1, checked: true  },
  { key: 'siege',           designation: 'Domiciliation / adresse siège social',         icon: 'landmark',     prix: 150000, qte: 1, checked: false },
  { key: 'business_plan',   designation: 'Business plan (étude financière & rentabilité)', icon: 'clipboard',    prix: 500000, qte: 1, checked: false },
  { key: 'etude_marche',    designation: 'Étude de marché',                              icon: 'bar-chart',    prix: 450000, qte: 1, checked: false },
  { key: 'site_internet',   designation: 'Site internet vitrine (5-8 pages)',            icon: 'globe',        prix: 800000, qte: 1, checked: false },
  { key: 'site_ecom',       designation: 'Site e-commerce complet',                      icon: 'globe',        prix: 1800000, qte: 1, checked: false },
  { key: 'crm',             designation: 'CRM sur mesure (déploiement + formation)',     icon: 'database',     prix: 2500000, qte: 1, checked: false },
  { key: 'branding',        designation: 'Identité visuelle (logo + charte + kit)',      icon: 'rocket',       prix: 350000, qte: 1, checked: false },
  { key: 'accompagnement',  designation: 'Accompagnement post-création (3 mois)',         icon: 'shield-check', prix: 400000, qte: 1, checked: false },
];

const DEFAULT_OPTIONS = [
  { key: 'urgence',        designation: 'Traitement express (formalités < 1 semaine)', prix: 250000, qte: 1, checked: false },
  { key: 'hebergement',    designation: 'Hébergement web + nom de domaine (1 an)',      prix: 150000, qte: 1, checked: false },
  { key: 'seo',            designation: 'Pack SEO de démarrage (référencement Google)',  prix: 200000, qte: 1, checked: false },
  { key: 'formation',      designation: 'Formation utilisateur CRM (1 jour)',            prix: 180000, qte: 1, checked: false },
];

// ─── Packs prédéfinis (prix promo) ────────────────────────────────────────
const PACKS = [
  {
    key: 'starter',
    titre: 'Pack Starter',
    tag: '-15%',
    color: 'from-emerald-500 to-teal-600',
    description: 'RCCM + NIF + Statuts',
    prestations: ['rccm', 'nif', 'statuts'],
    remise_pct: 15,
  },
  {
    key: 'digital',
    titre: 'Pack Digital',
    tag: '-10%',
    color: 'from-sky-500 to-indigo-600',
    description: 'Site internet + CRM sur mesure',
    prestations: ['site_internet', 'crm'],
    remise_pct: 10,
  },
  {
    key: 'complet',
    titre: 'Pack Complet',
    tag: '-20%',
    color: 'from-purple-500 to-fuchsia-600',
    description: 'Toutes les prestations essentielles',
    prestations: ['rccm', 'nif', 'consulaire', 'statuts', 'siege', 'business_plan', 'etude_marche', 'site_internet', 'crm', 'branding', 'accompagnement'],
    remise_pct: 20,
  },
];

// ─── Docs à fournir selon la forme juridique ──────────────────────────────
const DOCS_PAR_FORME = {
  'SARL': [
    "Copie CNI/passeport des associés",
    "Justificatif de domicile (< 3 mois)",
    "Attestation de dépôt du capital social (banque)",
    "Casier judiciaire du gérant (< 3 mois)",
    "Statuts signés (fournis par nos soins)",
    "Formulaire M0 rempli",
  ],
  'SARLU (unipersonnelle)': [
    "Copie CNI/passeport de l'associé unique",
    "Justificatif de domicile (< 3 mois)",
    "Attestation de dépôt du capital",
    "Casier judiciaire (< 3 mois)",
    "Statuts unipersonnels",
  ],
  'SA': [
    "CNI/passeports de tous les actionnaires (min. 7)",
    "Justificatifs de domicile",
    "Attestation de dépôt du capital (min. 10M XAF)",
    "Statuts avec conseil d'administration",
    "Casiers judiciaires (dirigeants)",
    "PV de l'assemblée constitutive",
  ],
  'SAS': [
    "CNI/passeports des associés",
    "Justificatifs de domicile",
    "Attestation de dépôt du capital",
    "Statuts SAS + règlement intérieur (option)",
    "Casier judiciaire du président",
  ],
  'Entreprise individuelle': [
    "Copie CNI/passeport de l'entrepreneur",
    "Justificatif de domicile",
    "Casier judiciaire (< 3 mois)",
    "Attestation d'activité (secteur réglementé)",
  ],
  'GIE': [
    "CNI/passeports des membres (min. 2)",
    "Statuts du groupement",
    "Justificatifs de domicile",
    "PV de constitution",
  ],
  'ONG / Association': [
    "CNI/passeports des membres fondateurs",
    "Statuts de l'association",
    "PV de l'assemblée constitutive",
    "Justificatif du siège social",
    "Récépissé de dépôt Ministère",
  ],
  'Autre': [
    "Nous vous communiquerons la liste précise selon votre projet.",
  ],
};

// ─── Templates CRM (accélérateurs) ────────────────────────────────────────
const CRM_TEMPLATES = [
  { key: 'immobilier', titre: 'CRM Immobilier', description: 'Biens, mandats, visites, contrats de bail', icon: 'landmark' },
  { key: 'commerce',   titre: 'CRM Commerce',   description: 'Clients, produits, ventes, stock, factures', icon: 'receipt' },
  { key: 'formation',  titre: 'CRM Formation',  description: 'Étudiants, formateurs, cours, présences', icon: 'clipboard' },
  { key: 'sante',      titre: 'CRM Santé',      description: 'Patients, RDV, dossiers médicaux, ordonnances', icon: 'shield-check' },
];

// ─── Statuts possibles par prestation ─────────────────────────────────────
const STATUTS = [
  { value: 'a_lancer',  label: 'À lancer',  color: 'bg-slate-100 text-slate-600' },
  { value: 'en_cours',  label: 'En cours',  color: 'bg-amber-100 text-amber-700' },
  { value: 'depose',    label: 'Déposé',    color: 'bg-blue-100 text-blue-700' },
  { value: 'obtenu',    label: 'Obtenu',    color: 'bg-emerald-100 text-emerald-700' },
];

const ICON_MAP = {
  'scale': Scale,
  'receipt': Receipt,
  'award': Award,
  'file-signature': FileSignature,
  'landmark': Landmark,
  'clipboard': ClipboardList,
  'bar-chart': BarChart3,
  'globe': Globe,
  'database': Database,
  'rocket': Rocket,
  'shield-check': ShieldCheck,
};

export default function CreationEntrepriseDevisModal({ open, onOpenChange, onSaved }) {
  const [prospects, setProspects] = useState([]);
  const [loadingProspects, setLoadingProspects] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [showNewEnterprise, setShowNewEnterprise] = useState(false);
  const [newEnt, setNewEnt] = useState({ entreprise: '', contact: '', email: '', telephone: '', adresse: '' });
  const [savingNewEnt, setSavingNewEnt] = useState(false);

  const [projet, setProjet] = useState({
    objectif: 'Immatriculation de la société',
    forme_juridique: 'SARL',
    secteur: 'Commerce & distribution',
    delai: '2 - 3 semaines',
    capital_social: '',
    date_lancement: '',
  });

  const [prestations, setPrestations] = useState(DEFAULT_PRESTATIONS.map((p) => ({ ...p, statut: 'a_lancer', date_effective: '' })));
  const [options, setOptions] = useState(DEFAULT_OPTIONS.map((o) => ({ ...o })));
  const [transport, setTransport] = useState({ mode: 'Livraison numérique (email/lien)', lieu: '', prix: 0 });
  const [notes, setNotes] = useState('');
  const [validiteDate, setValiditeDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [remisePackPct, setRemisePackPct] = useState(0);
  const [remisePackKey, setRemisePackKey] = useState('');
  const [selectedCrmTemplate, setSelectedCrmTemplate] = useState('');

  useEffect(() => {
    if (!open) return;
    setSearch('');
    setSelectedProspect(null);
    setPrestations(DEFAULT_PRESTATIONS.map((p) => ({ ...p, statut: 'a_lancer', date_effective: '' })));
    setOptions(DEFAULT_OPTIONS.map((o) => ({ ...o })));
    setTransport({ mode: 'Livraison numérique (email/lien)', lieu: '', prix: 0 });
    setNotes('');
    setRemisePackPct(0);
    setRemisePackKey('');
    setSelectedCrmTemplate('');
    const d = new Date();
    d.setDate(d.getDate() + 30);
    setValiditeDate(d.toISOString().slice(0, 10));
    const d2 = new Date();
    d2.setDate(d2.getDate() + 21);
    setProjet((p) => ({ ...p, date_lancement: d2.toISOString().slice(0, 10) }));

    const load = async () => {
      setLoadingProspects(true);
      try {
        const r = await axios.get('/prospects');
        setProspects(r.data || []);
      } catch (e) {
        toast.error('Impossible de charger les prospects');
      } finally {
        setLoadingProspects(false);
      }
    };
    load();
  }, [open]);

  const filteredProspects = useMemo(() => {
    if (!search) return prospects;
    const t = search.toLowerCase();
    return prospects.filter((p) =>
      (p.entreprise || '').toLowerCase().includes(t) ||
      (p.contact || '').toLowerCase().includes(t) ||
      (p.email || '').toLowerCase().includes(t) ||
      (p.telephone || '').toLowerCase().includes(t)
    );
  }, [prospects, search]);

  const updatePresta = (key, field, value) => {
    setPrestations((prev) => prev.map((it) => (it.key === key ? { ...it, [field]: value } : it)));
  };
  const removePresta = (key) => setPrestations((prev) => prev.filter((it) => it.key !== key));
  const addPresta = () => {
    const name = window.prompt('Nom de la prestation à ajouter :');
    if (!name || !name.trim()) return;
    setPrestations((prev) => [
      ...prev,
      { key: `custom_${Date.now()}`, designation: name.trim(), icon: 'briefcase', prix: 0, qte: 1, checked: true },
    ]);
  };
  const updateOption = (key, field, value) => {
    setOptions((prev) => prev.map((it) => (it.key === key ? { ...it, [field]: value } : it)));
  };

  const checkedPrestations = prestations.filter((p) => p.checked && (Number(p.qte) || 0) > 0);
  const checkedOptions = options.filter((o) => o.checked && (Number(o.qte) || 0) > 0);
  const sousTotalProd = checkedPrestations.reduce((s, it) => s + (Number(it.prix) || 0) * (Number(it.qte) || 0), 0);
  const sousTotalOpt = checkedOptions.reduce((s, it) => s + (Number(it.prix) || 0) * (Number(it.qte) || 0), 0);
  const transportPrix = Number(transport.prix) || 0;
  const remiseMontant = (sousTotalProd + sousTotalOpt) * (Number(remisePackPct) / 100);
  const totalGeneral = sousTotalProd + sousTotalOpt + transportPrix - remiseMontant;
  const [tva, setTva] = useTva();
  const tvaMt = tvaAmount(totalGeneral, tva);
  const canSave = selectedProspect && checkedPrestations.length > 0;

  const applyPack = (pack) => {
    setPrestations((prev) => prev.map((it) => ({
      ...it,
      checked: pack.prestations.includes(it.key) ? true : it.checked,
    })));
    setRemisePackPct(pack.remise_pct);
    setRemisePackKey(pack.key);
    toast.success(`${pack.titre} appliqué (-${pack.remise_pct}%)`);
  };

  const clearPack = () => {
    setRemisePackPct(0);
    setRemisePackKey('');
  };

  const save = async () => {
    if (!selectedProspect) { toast.error('Sélectionnez d\'abord une entreprise'); return; }
    if (checkedPrestations.length === 0) { toast.error('Cochez au moins une prestation'); return; }
    setSaving(true);
    try {
      const items = [
        ...checkedPrestations.map((p) => ({
          designation: p.designation
            + (p.statut && p.statut !== 'a_lancer' ? ` [${STATUTS.find(s => s.value === p.statut)?.label || p.statut}${p.date_effective ? ' · ' + p.date_effective : ''}]` : ''),
          quantite: parseInt(p.qte, 10) || 1,
          prix_unitaire: parseFloat(p.prix) || 0,
        })),
        ...checkedOptions.map((o) => ({
          designation: `[Option] ${o.designation}`,
          quantite: parseInt(o.qte, 10) || 1,
          prix_unitaire: parseFloat(o.prix) || 0,
        })),
      ];
      // Ligne "Remise pack" négative
      if (remiseMontant > 0) {
        const packLabel = PACKS.find(p => p.key === remisePackKey)?.titre || 'Pack';
        items.push({
          designation: `Remise ${packLabel} (-${remisePackPct}%)`,
          quantite: 1,
          prix_unitaire: -Math.round(remiseMontant),
        });
      }
      const notesFull = [
        `Objectif : ${projet.objectif}`,
        `Forme juridique : ${projet.forme_juridique}`,
        `Secteur : ${projet.secteur}`,
        `Délai : ${projet.delai}`,
        `Capital social : ${projet.capital_social ? projet.capital_social + ' XAF' : '—'}`,
        `Date de lancement souhaitée : ${projet.date_lancement || '—'}`,
        remisePackKey ? `Pack appliqué : ${PACKS.find(p => p.key === remisePackKey)?.titre} (-${remisePackPct}%)` : '',
        selectedCrmTemplate ? `Template CRM : ${CRM_TEMPLATES.find(c => c.key === selectedCrmTemplate)?.titre}` : '',
        '',
        '── Suivi des formalités ──',
        ...checkedPrestations.map(p => `• ${p.designation} : ${STATUTS.find(s => s.value === p.statut)?.label || 'À lancer'}${p.date_effective ? ' (' + p.date_effective + ')' : ''}`),
        '',
        '── Pièces à fournir ──',
        ...(DOCS_PAR_FORME[projet.forme_juridique] || []).map(d => `• ${d}`),
        notes ? `\n${notes}` : '',
      ].filter(Boolean).join('\n');
      const payload = {
        service_id: 'creation-entreprise',
        service_titre: "Création d'entreprise",
        client_nom: selectedProspect.entreprise || '',
        client_email: selectedProspect.email || '',
        client_telephone: selectedProspect.telephone || '',
        client_adresse: selectedProspect.adresse || '',
        client_prospect_id: selectedProspect.id || null,
        supports_produits: items,
        transport_mode: transport.mode,
        transport_lieu: transport.lieu,
        transport_prix: transportPrix,
        notes: notesFull,
        validite_date: validiteDate,
        modele: "Création d'entreprise",
        prix_unitaire: 0,
        quantite: 0,
        remise_pct: 0,
        tva_pct: tvaPct(tva),
        prestations: [],
      };
      const r = await axios.post('/conception/devis', payload);
      toast.success(`Devis ${r.data.numero} généré`);
      onSaved && onSaved(r.data);
      onOpenChange(false);
    } catch (e) {
      toast.error('Erreur lors de la génération du devis');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[1500px] w-[98vw] h-[93vh] p-0 overflow-hidden flex flex-col"
        data-testid="creation-entreprise-modal"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b bg-white sticky top-0 z-10">
          <div>
            <div className="text-xs text-slate-500 font-medium">Conception ▸ Devis</div>
            <h2 className="text-2xl font-bold text-slate-900">Nouveau devis — Création d&apos;entreprise</h2>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="btn-back-creation-devis">
              <ArrowLeft className="h-4 w-4 mr-1" /> Retour
            </Button>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Enregistrer brouillon</Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={save}
              disabled={!canSave || saving}
              data-testid="btn-generate-creation-devis"
            >
              <FileText className="h-4 w-4 mr-1.5" />
              {saving ? 'Génération…' : 'Générer le devis'}
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-hidden grid grid-cols-1 lg:grid-cols-[1fr_400px]">
          <div className="overflow-y-auto p-5 space-y-5 bg-slate-50">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* 1. Prospects */}
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <div className="text-sm font-bold text-slate-800 mb-3">1. Choisir l&apos;entreprise (prospect)</div>
                <div className="relative mb-2">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                  <Input className="pl-8" placeholder="Rechercher…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="cre-input-search-prospect" />
                </div>
                <Button
                  type="button"
                  size="sm"
                  className="w-full mb-2 bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5"
                  onClick={() => { setShowNewEnterprise(true); setNewEnt({ entreprise: '', contact: '', email: '', telephone: '', adresse: '' }); }}
                  data-testid="cre-btn-new-enterprise"
                >
                  <Plus className="h-3.5 w-3.5" /> Nouvelle entreprise
                </Button>
                <div className="max-h-56 overflow-y-auto space-y-1.5 pr-1">
                  {loadingProspects && <div className="text-xs text-slate-500 p-2">Chargement…</div>}
                  {!loadingProspects && filteredProspects.length === 0 && (
                    <div className="text-xs text-slate-500 p-2 italic">Aucun prospect</div>
                  )}
                  {filteredProspects.map((p) => {
                    const sel = selectedProspect?.id === p.id;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setSelectedProspect(p)}
                        className={`w-full text-left rounded-lg p-2.5 border transition-colors ${sel ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-800 border-slate-200 hover:bg-slate-50'}`}
                        data-testid={`cre-prospect-item-${p.id}`}
                      >
                        <div className="text-sm font-semibold truncate">{p.entreprise || 'Sans nom'}</div>
                        <div className={`text-xs truncate ${sel ? 'text-blue-100' : 'text-slate-500'}`}>{p.email || '—'}</div>
                        <div className={`text-xs ${sel ? 'text-blue-100' : 'text-slate-500'}`}>{p.telephone || '—'}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* 2. Infos */}
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <div className="text-sm font-bold text-slate-800 mb-3">2. Informations de l&apos;entreprise</div>
                {selectedProspect ? (
                  <div className="space-y-2 text-sm">
                    <div className="flex gap-2"><span className="text-slate-500 w-20 shrink-0">Nom :</span><span className="font-semibold text-slate-800">{selectedProspect.entreprise || '—'}</span></div>
                    <div className="flex gap-2"><span className="text-slate-500 w-20 shrink-0">Contact :</span><span className="text-slate-800">{selectedProspect.contact || '—'}</span></div>
                    <div className="flex gap-2"><span className="text-slate-500 w-20 shrink-0">Email :</span><span className="text-blue-600 break-all">{selectedProspect.email || '—'}</span></div>
                    <div className="flex gap-2"><span className="text-slate-500 w-20 shrink-0">Téléphone :</span><span className="text-slate-800">{selectedProspect.telephone || '—'}</span></div>
                    <div className="flex gap-2"><span className="text-slate-500 w-20 shrink-0">Adresse :</span><span className="text-slate-800">{selectedProspect.adresse || '—'}</span></div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-40 text-slate-400 text-sm italic">
                    <Building2 className="h-8 w-8 mb-2" />
                    Sélectionnez une entreprise
                  </div>
                )}
              </div>

              {/* 3. Détails projet */}
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <div className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-1.5"><Briefcase className="h-4 w-4" />3. Détails du projet</div>
                <div className="space-y-2.5">
                  <div>
                    <Label className="text-xs">Objectif</Label>
                    <Select value={projet.objectif} onValueChange={(v) => setProjet({ ...projet, objectif: v })}>
                      <SelectTrigger data-testid="cre-objectif"><SelectValue /></SelectTrigger>
                      <SelectContent>{OBJECTIFS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Forme juridique</Label>
                    <Select value={projet.forme_juridique} onValueChange={(v) => setProjet({ ...projet, forme_juridique: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{FORMES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Secteur d&apos;activité</Label>
                    <Select value={projet.secteur} onValueChange={(v) => setProjet({ ...projet, secteur: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{SECTEURS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Délai souhaité</Label>
                    <Select value={projet.delai} onValueChange={(v) => setProjet({ ...projet, delai: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{DELAIS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Capital social (XAF)</Label>
                    <Input type="number" min={0} value={projet.capital_social} onChange={(e) => setProjet({ ...projet, capital_social: e.target.value })} placeholder="Ex. 1 000 000" />
                  </div>
                  <div>
                    <Label className="text-xs">Date de lancement souhaitée</Label>
                    <Input type="date" value={projet.date_lancement} onChange={(e) => setProjet({ ...projet, date_lancement: e.target.value })} />
                  </div>
                </div>
              </div>

              {/* 4. Transport */}
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <div className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-1.5"><Truck className="h-4 w-4" />4. Livraison</div>
                <div className="space-y-2.5">
                  <div>
                    <Label className="text-xs">Mode</Label>
                    <Select value={transport.mode} onValueChange={(v) => setTransport({ ...transport, mode: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{TRANSPORT_MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Adresse de livraison (documents)</Label>
                    <Input value={transport.lieu} onChange={(e) => setTransport({ ...transport, lieu: e.target.value })} placeholder="Ex. Bangui" />
                  </div>
                  <div>
                    <Label className="text-xs">Frais de dossier / livraison (XAF)</Label>
                    <Input type="number" min={0} value={transport.prix} onChange={(e) => setTransport({ ...transport, prix: e.target.value })} data-testid="cre-transport-prix" />
                  </div>
                  <div className="rounded-md bg-blue-50 border border-blue-100 text-blue-700 text-xs px-2.5 py-2">
                    ⓘ Si tout est numérique, laissez à 0.
                  </div>
                </div>
              </div>
            </div>

            {/* Packs prédéfinis */}
            <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="section-packs">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="text-sm font-bold text-slate-800">Packs à prix promo</div>
                {remisePackKey && (
                  <button type="button" onClick={clearPack} className="text-xs text-red-600 hover:underline">
                    Retirer le pack
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {PACKS.map((p) => {
                  const active = remisePackKey === p.key;
                  return (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => applyPack(p)}
                      className={`text-left rounded-xl p-4 border-2 transition-all overflow-hidden relative group ${
                        active ? 'border-emerald-500 ring-2 ring-emerald-200' : 'border-slate-200 hover:border-blue-400'
                      }`}
                      data-testid={`pack-${p.key}`}
                    >
                      <div className={`absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r ${p.color}`}></div>
                      <div className="flex items-start justify-between mt-1">
                        <div>
                          <div className="text-base font-bold text-slate-900">{p.titre}</div>
                          <div className="text-xs text-slate-500 mt-1">{p.description}</div>
                        </div>
                        <span className={`ml-2 text-xs font-bold px-2 py-0.5 rounded-full bg-gradient-to-r ${p.color} text-white shrink-0`}>
                          {p.tag}
                        </span>
                      </div>
                      <div className="mt-3 text-[11px] text-slate-500">
                        {p.prestations.length} prestation{p.prestations.length > 1 ? 's' : ''} incluse{p.prestations.length > 1 ? 's' : ''}
                      </div>
                      {active && (
                        <div className="mt-2 text-xs font-semibold text-emerald-700">
                          ✓ Pack appliqué
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Docs à fournir selon la forme juridique */}
            <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="section-docs">
              <div className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-1.5">
                <FileText className="h-4 w-4" />
                Pièces à fournir — <span className="text-blue-700">{projet.forme_juridique}</span>
              </div>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                {(DOCS_PAR_FORME[projet.forme_juridique] || []).map((d, i) => (
                  <li key={i} className="flex items-start gap-2 p-2 rounded-md bg-slate-50/60 border border-slate-100">
                    <div className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5">{i + 1}</div>
                    <span className="text-slate-700 leading-tight">{d}</span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-slate-500 mt-2 italic">
                Cette liste sera automatiquement ajoutée aux notes du devis généré.
              </p>
            </div>

            {/* 5. Prestations */}
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="text-sm font-bold text-slate-800">5. Prestations création d&apos;entreprise</div>
                <Button onClick={addPresta} className="bg-blue-600 hover:bg-blue-700 text-white" size="sm">
                  <Plus className="h-4 w-4 mr-1" /> Ajouter une prestation
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
                {prestations.map((it) => {
                  const Icon = ICON_MAP[it.icon] || Briefcase;
                  return (
                    <div
                      key={it.key}
                      className={`border rounded-xl p-3 transition-all ${it.checked ? 'border-blue-500 bg-blue-50/40 ring-1 ring-blue-200' : 'border-slate-200 bg-white'}`}
                      data-testid={`cre-presta-${it.key}`}
                    >
                      <label className="flex items-start gap-2 cursor-pointer mb-2">
                        <Checkbox
                          checked={it.checked}
                          onCheckedChange={(v) => updatePresta(it.key, 'checked', !!v)}
                          data-testid={`cre-check-${it.key}`}
                          className="mt-0.5"
                        />
                        <div className="flex-1 flex items-start gap-2">
                          <Icon className="h-4 w-4 text-slate-500 mt-0.5" />
                          <span className="text-sm font-medium text-slate-800 leading-tight">{it.designation}</span>
                        </div>
                      </label>
                      <div className="space-y-1.5">
                        <div>
                          <Label className="text-[10px] text-slate-500">Quantité</Label>
                          <Input type="number" min={0} value={it.qte} onChange={(e) => updatePresta(it.key, 'qte', e.target.value)} className="h-8 text-sm text-right" />
                        </div>
                        <div>
                          <Label className="text-[10px] text-slate-500">Prix unitaire (XAF)</Label>
                          <Input type="number" min={0} value={it.prix} onChange={(e) => updatePresta(it.key, 'prix', e.target.value)} className="h-8 text-sm text-right" />
                        </div>
                        {it.checked && (
                          <>
                            <div>
                              <Label className="text-[10px] text-slate-500">Statut</Label>
                              <Select value={it.statut || 'a_lancer'} onValueChange={(v) => updatePresta(it.key, 'statut', v)}>
                                <SelectTrigger className="h-8 text-xs" data-testid={`cre-statut-${it.key}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {STATUTS.map((s) => (
                                    <SelectItem key={s.value} value={s.value}>
                                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold ${s.color}`}>{s.label}</span>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-[10px] text-slate-500">Date effective</Label>
                              <Input type="date" value={it.date_effective || ''} onChange={(e) => updatePresta(it.key, 'date_effective', e.target.value)} className="h-8 text-xs" data-testid={`cre-date-eff-${it.key}`} />
                            </div>
                          </>
                        )}
                      </div>
                      <div className="flex justify-end mt-1.5">
                        <button className="text-red-500 hover:text-red-700" onClick={() => removePresta(it.key)} title="Retirer">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Templates CRM (visible si la prestation CRM est cochée) */}
            {prestations.find((p) => p.key === 'crm' && p.checked) && (
              <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="section-crm-templates">
                <div className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-1.5">
                  <Database className="h-4 w-4" />
                  Choisir un template CRM (accélérateur)
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {CRM_TEMPLATES.map((t) => {
                    const TIcon = ICON_MAP[t.icon] || Database;
                    const active = selectedCrmTemplate === t.key;
                    return (
                      <button
                        key={t.key}
                        type="button"
                        onClick={() => setSelectedCrmTemplate(active ? '' : t.key)}
                        className={`text-left rounded-lg p-3 border-2 transition-all ${
                          active ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' : 'border-slate-200 bg-white hover:border-blue-300'
                        }`}
                        data-testid={`crm-template-${t.key}`}
                      >
                        <div className="w-9 h-9 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center mb-2">
                          <TIcon className="h-5 w-5" />
                        </div>
                        <div className="text-sm font-bold text-slate-900">{t.titre}</div>
                        <div className="text-[10px] text-slate-500 leading-tight mt-1">{t.description}</div>
                        {active && <div className="text-[10px] text-blue-700 mt-1 font-semibold">✓ Sélectionné</div>}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Options supplémentaires */}
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="text-sm font-bold text-slate-800 mb-3">Options supplémentaires</div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                {options.map((op) => (
                  <div key={op.key} className={`border rounded-lg p-3 ${op.checked ? 'border-blue-400 bg-blue-50/40' : 'border-slate-200 bg-white'}`}>
                    <label className="flex items-center gap-2 cursor-pointer mb-2">
                      <Checkbox checked={op.checked} onCheckedChange={(v) => updateOption(op.key, 'checked', !!v)} />
                      <span className="text-sm font-medium text-slate-800 leading-tight">{op.designation}</span>
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[10px] text-slate-500">Qté</Label>
                        <Input type="number" min={0} value={op.qte} onChange={(e) => updateOption(op.key, 'qte', e.target.value)} className="h-8 text-sm text-right" />
                      </div>
                      <div>
                        <Label className="text-[10px] text-slate-500">Prix</Label>
                        <Input type="number" min={0} value={op.prix} onChange={(e) => updateOption(op.key, 'prix', e.target.value)} className="h-8 text-sm text-right" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Récapitulatif */}
          <div className="border-l border-slate-200 bg-white flex flex-col overflow-hidden">
            <div className="p-5 border-b sticky top-0 bg-white">
              <h3 className="text-lg font-bold text-slate-900">Récapitulatif du devis</h3>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="cre-recap-panel">
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr className="text-slate-500 text-xs uppercase">
                      <th className="text-left px-3 py-2 font-semibold">Désignation</th>
                      <th className="text-right px-2 py-2 font-semibold w-14">Qté</th>
                      <th className="text-right px-2 py-2 font-semibold w-20">PU</th>
                      <th className="text-right px-3 py-2 font-semibold w-24">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {checkedPrestations.length + checkedOptions.length === 0 && (
                      <tr><td colSpan={4} className="text-center py-6 text-slate-400 italic text-xs">Cochez au moins une prestation</td></tr>
                    )}
                    {checkedPrestations.map((it) => (
                      <tr key={it.key} className="border-t border-slate-100">
                        <td className="px-3 py-2 text-slate-800 leading-tight" title={it.designation}>{it.designation}</td>
                        <td className="px-2 py-2 text-right text-slate-800">{it.qte}</td>
                        <td className="px-2 py-2 text-right text-slate-800">{fmtXAF(it.prix)}</td>
                        <td className="px-3 py-2 text-right font-semibold text-slate-900">{fmtXAF(Number(it.prix) * Number(it.qte))}</td>
                      </tr>
                    ))}
                    {checkedOptions.map((it) => (
                      <tr key={it.key} className="border-t border-slate-100 bg-slate-50/30">
                        <td className="px-3 py-2 text-slate-700 text-xs italic">Option : {it.designation}</td>
                        <td className="px-2 py-2 text-right text-slate-700">{it.qte}</td>
                        <td className="px-2 py-2 text-right text-slate-700">{fmtXAF(it.prix)}</td>
                        <td className="px-3 py-2 text-right font-semibold text-slate-800">{fmtXAF(Number(it.prix) * Number(it.qte))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between border-t pt-3">
                  <span className="text-slate-600">Sous-total prestations</span>
                  <span className="font-semibold text-slate-900" data-testid="cre-recap-sous-total-prod">{fmtXAF(sousTotalProd)}</span>
                </div>
                {sousTotalOpt > 0 && (
                  <div className="flex justify-between">
                    <span className="text-slate-600">Sous-total options</span>
                    <span className="font-semibold text-slate-900">{fmtXAF(sousTotalOpt)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-slate-600">Frais dossier / livraison</span>
                  <span className="font-semibold text-slate-900">{fmtXAF(transportPrix)}</span>
                </div>
                {remiseMontant > 0 && (
                  <div className="flex justify-between text-emerald-700">
                    <span className="font-semibold">
                      Remise {PACKS.find(p => p.key === remisePackKey)?.titre || 'pack'} (-{remisePackPct}%)
                    </span>
                    <span className="font-bold">-{fmtXAF(remiseMontant)}</span>
                  </div>
                )}
                <TvaToggle tva={tva} setTva={setTva} base={totalGeneral} testid="cre-tva" />
                <div className="flex justify-between pt-3 border-t text-base">
                  <span className="font-bold text-slate-900">{tva.on ? 'Total TTC (XAF)' : 'Total général (XAF)'}</span>
                  <span className="font-bold text-blue-700 text-xl" data-testid="cre-recap-total">{fmtXAF(totalGeneral + tvaMt)}</span>
                </div>
              </div>

              <div>
                <Label>Notes</Label>
                <Textarea rows={3} placeholder="Ajoutez une note (optionnel)…" value={notes} onChange={(e) => setNotes(e.target.value)} />
              </div>
              <div>
                <Label>Validité du devis</Label>
                <Input type="date" value={validiteDate} onChange={(e) => setValiditeDate(e.target.value)} />
              </div>
            </div>
          </div>
        </div>
      </DialogContent>

      {/* Sous-dialog : Nouvelle entreprise (création rapide d'un prospect) */}
      <Dialog open={showNewEnterprise} onOpenChange={setShowNewEnterprise}>
        <DialogContent className="max-w-lg" data-testid="cre-new-enterprise-dialog">
          <div className="p-1">
            <h2 className="text-lg font-bold text-slate-900 mb-1">Nouvelle entreprise</h2>
            <p className="text-xs text-slate-500 mb-4">Créez une entreprise à la volée si elle n'existe pas encore dans vos prospects.</p>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Nom de l'entreprise <span className="text-rose-500">*</span></Label>
                <Input value={newEnt.entreprise} onChange={(e) => setNewEnt({ ...newEnt, entreprise: e.target.value })} placeholder="Ex : ACME SARL" data-testid="cre-newent-nom" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Contact</Label>
                  <Input value={newEnt.contact} onChange={(e) => setNewEnt({ ...newEnt, contact: e.target.value })} placeholder="Nom du responsable" data-testid="cre-newent-contact" />
                </div>
                <div>
                  <Label className="text-xs">Téléphone</Label>
                  <Input value={newEnt.telephone} onChange={(e) => setNewEnt({ ...newEnt, telephone: e.target.value })} placeholder="+236 …" data-testid="cre-newent-tel" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Email</Label>
                <Input type="email" value={newEnt.email} onChange={(e) => setNewEnt({ ...newEnt, email: e.target.value })} placeholder="contact@entreprise.com" data-testid="cre-newent-email" />
              </div>
              <div>
                <Label className="text-xs">Adresse</Label>
                <Input value={newEnt.adresse} onChange={(e) => setNewEnt({ ...newEnt, adresse: e.target.value })} placeholder="Ville, quartier…" data-testid="cre-newent-adresse" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="outline" onClick={() => setShowNewEnterprise(false)} disabled={savingNewEnt}>Annuler</Button>
              <Button
                className="bg-emerald-600 hover:bg-emerald-700"
                disabled={!newEnt.entreprise.trim() || savingNewEnt}
                onClick={async () => {
                  setSavingNewEnt(true);
                  try {
                    const r = await axios.post('/prospects', {
                      entreprise: newEnt.entreprise.trim(),
                      contact: newEnt.contact.trim(),
                      email: newEnt.email.trim(),
                      telephone: newEnt.telephone.trim(),
                      adresse: newEnt.adresse.trim(),
                      statut: 'Nouveau',
                    });
                    const created = r.data;
                    setProspects((prev) => [created, ...prev]);
                    setSelectedProspect(created);
                    setShowNewEnterprise(false);
                    toast.success('Entreprise créée et sélectionnée');
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Impossible de créer l'entreprise");
                  } finally {
                    setSavingNewEnt(false);
                  }
                }}
                data-testid="cre-newent-save"
              >
                {savingNewEnt ? 'Création…' : 'Créer & sélectionner'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
